import { MEMBERSHIP } from '~/components/dialogs/dialog-selector';
import {
  HALFMOON_SOLAR,
  OREGON_DISCLAIMER,
  SELECT_UTILITY_IMAGE,
  WIND_FARM,
} from '~/components/image';
import { MembershipFunnelContext } from '~/machines/membership-funnel';
import { MembershipScreenMap } from '~/machines/membership-funnel/screen-map';
import { ScreenMapCopy } from '~/machines/utils/types';
import { BillingCopy, BillingContext } from '~/screens/billing';
import { BillingSelectionContext, BillingSelectionCopy } from '~/screens/billing-selection';
import { CreateAccountCopy, CreateAccountContext } from '~/screens/create-account';
import {
  CreateAccountGoogleContext,
  CreateAccountGoogleCopy,
} from '~/screens/create-account-google';
import { CredentialsContext, CredentialsCopy } from '~/screens/credentials';
import { SelectUtilityCopy } from '~/screens/select-utility';

export const copy: ScreenMapCopy<MembershipScreenMap> = {
  utility: {
    utility: (): SelectUtilityCopy => ({
      title: 'Let’s see what renewable energy options are available in your area.',
      description:
        'Enter your zip code and utility provider to see how you can support wind and solar energy.',
      descriptionTwo: '',
      imageType: WIND_FARM,
      next: 'Continue',
      zipCodeLabel: 'Zip code',
      zipCodePlaceholder: 'Enter your zip code',
    }),
  },
  user: {
    user: (context: CreateAccountContext): Partial<CreateAccountCopy> => ({
      title:
        'Community Solar is not yet available in your area, but you can still power your home using clean energy.',
      description:
        'For only $5/mo you can sign up for an Arcadia membership and power your home with renewable energy, without changing your electricity service.',
      descriptionTwo:
        'Arcadia partners with renewable energy suppliers to source, verify, purchase and retire RECs on your behalf.',
      next: 'Continue',
      learnMoreModal: MEMBERSHIP,
      learnMoreText: 'Why sign up for an Arcadia Membership?',
      imageType: WIND_FARM,
      disclaimer: `Arcadia is a service provider independent of, and not affiliated with, ${context.utility.displayName}. ${context.utility.displayName} has not endorsed this service.`,
      oregonDisclaimerImageType: OREGON_DISCLAIMER,
      oregonDisclaimerText:
        'This organization is operating under the Oregon Community Solar Program. The Oregon Community Solar Program is a state-enabled initiative overseen by the Oregon Public Utility Commission and implemented by Energy Solutions, The Energy Trust of Oregon, and Community Energy Project, and Portland General Electric. The Program provides resources and certification for its Project Managers (PMs) but does not promote any individual PM or PM affiliated organization. For more information about the program, call 1-800-481-0510 or email ',
      virginiaInformation:
        "We're thrilled to connect you to a Community Solar project! Currently, we're prioritizing sign-ups for low to moderate income households. If you don't fit this criteria, please be aware of a potentially longer wait time. Thank you for your understanding and enthusiasm.",
    }),
    google: (context: CreateAccountGoogleContext): Partial<CreateAccountGoogleCopy> => ({
      title:
        'Community Solar is not yet available in your area, but you can still power your home using clean energy.',
      description:
        'For only $5/mo you can sign up for an Arcadia membership and power your home with renewable energy, without changing your electricity service.',
      descriptionTwo:
        'Arcadia partners with renewable energy suppliers to source, verify, purchase and retire RECs on your behalf.',
      next: 'Continue',
      titleForm: 'Confirm your info',
      learnMoreModal: MEMBERSHIP,
      learnMoreText: 'Why sign up for an Arcadia Membership?',
      imageType: WIND_FARM,
      disclaimer: `Arcadia is a service provider independent of, and not affiliated with, ${context.utility.displayName}. ${context.utility.displayName} has not endorsed this service.`,
    }),
  },
  enrollment: {
    utilityConnect: (context: CredentialsContext): Partial<CredentialsCopy> => {
      const utility = context.utility.displayName;
      return {
        description: `You’ll still be a ${utility} customer, but we’ll keep track of your bill and use energy from clean wind farms.`,
        invalidDescriptionOne: `There was an error with the information you provided for your ${utility} account.`,
        invalidDescriptionTwo:
          'Syncing with your utility helps us understand the amount of energy you use so that we can match it with energy from clean wind farms. Establishing this connection does not change anything about your utility account or how you get energy.',
        invalidDescriptionThree: '',
        learnMoreModal: '',
        learnMoreText: '',
        imageType: HALFMOON_SOLAR,
        disclaimer:
          'Arcadia is an independent service provider committed to putting you and the planet first. Arcadia is not affiliated with any utility or governmental agency.',
        syncUtilityButton: 'Enter utility login credentials',
        accountNumberButton: 'Enter utility account information', // NB: won't ever show up
        billUploadButton: 'Upload a picture of your bill', // NB: won't ever show up
      };
    },
  },
  billingExperience: (context: BillingSelectionContext): BillingSelectionCopy => {
    const utility = context.utility.displayName;
    return {
      title: 'Now, choose your billing preference.',
      description: '',
      acb: {
        description: `You’ll pay your ${utility} bill through Arcadia’s secure platform. We’ll match your monthly usage with clean energy from wind farms. We’ll also send you a monthly clean energy statement that summarizes your usage and impact.`,
        title: 'Pay through Arcadia',
      },
      ucb: {
        description: `Keep paying your ${utility} bill like you do now. We’ll purchase a fixed amount of clean energy from wind farms each month. We'll send you a monthly clean energy statement that summarizes your impact.`,
        title: `Pay through ${utility}`,
      },
      dual: {
        description: '',
        title: '',
      },
      imageType: SELECT_UTILITY_IMAGE,
      submit: 'Continue',
    };
  },
  billing: {
    payment: (context: MembershipBillingContext): BillingCopy => {
      const utility = context.utility.displayName;
      const company = context.sponsoredBy || 'your company';

      const acbDescription = context.sponsored
        ? `As an employee of ${company}, your Arcadia membership is covered. Arcadia will use the payment method you link now to automatically pay your ${utility} bill every month.`
        : `Your Arcadia membership is free for your first month and $5/mo after that. Arcadia will use the payment method you link now to automatically pay your ${utility} bill every month.`;

      const ucbDescription = context.sponsored
        ? `As an employee of ${company}, your Arcadia membership is covered. Your payment method won't be used unless you choose to upgrade your Arcadia membership.`
        : 'Your Arcadia membership is free for your first month and $5/mo after that. Arcadia will use the payment method you link now to automatically pay for your membership and clean wind energy every month.';

      return {
        title: 'Now, set up autopay.',
        description: context.experience === 'acb' ? acbDescription : ucbDescription,
        learnMoreModal: MEMBERSHIP,
        imageType: WIND_FARM,
        learnMoreText: 'Learn more',
        payArcadiaText: '', // NB: shouldn't ever show up (for dual only)
        payUtilityText: '', // NB: shouldn't ever show up (for dual only)
        acbCalloutHeader: 'Get ready for your first bill',
        acbCalloutBody: `Please be sure to pay any remaining balance on your ${utility} account and cancel automatic bill payments through ${utility} if you had them turned on. Don't worry. We'll send you a reminder to your email after you complete signing up.`,
        paymentHoldNotice: '',
      };
    },
  },
};

interface MembershipBillingContext
  extends BillingContext,
    Pick<MembershipFunnelContext, 'sponsored' | 'sponsoredBy'> {}
